import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { DocumentDownloadOptions, DownloadFormat, StyleFormat } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { createFilterBody } from '@/helpers/createFilterParams';
import { FactFilters } from '@/types';

interface UseFactsDownloadProps {
  matterId: string;
  subsetId: string;
}

interface ExportResponse {
  exportId: string;
  jobId: string;
}

interface DownloadUrlResponse {
  downloadUrl: string;
}

const useDownloadFacts = ({ matterId, subsetId }: UseFactsDownloadProps) => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadFacts = async ({
    title,
    columns,
    downloadFormat,
    styleFormat,
    style,
    documentOptions,
    filters,
    includeSourceDocuments = false,
    orderByDate,
  }: {
    downloadFormat: DownloadFormat;
    styleFormat?: StyleFormat;
    title: string;
    columns?: string[];
    style?: string;
    documentOptions?: DocumentDownloadOptions;
    filters: FactFilters;
    includeSourceDocuments?: boolean;
    orderByDate?: boolean;
  }): Promise<ExportResponse> => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: {
        downloadFormat,
        styleFormat,
        title,
        columns,
        style,
        documentOptions,
        includeSourceDocuments,
        orderByDate,
        ...createFilterBody(filters),
      },
    });

    const endpoint = includeSourceDocuments
      ? `${APIBaseChronos}/api/matter/${matterId}/fact/${subsetId}/download-with-documents`
      : `${APIBaseChronos}/api/matter/${matterId}/fact/${subsetId}/download`;

    const response = await fetch(endpoint, fetchConfig);

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.message ?? 'Failed to start facts export');
    }

    return await response.json();
  };

  const downloadExport = async (title: string, exportId: string, format?: DownloadFormat): Promise<void> => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.GET,
    });

    const formatParam = format ? `&format=${format}` : '';
    const endpoint = `${APIBaseChronos}/api/matter/${matterId}/fact/${subsetId}/export-download-url?exportId=${exportId}${formatParam}`;
    const response = await fetch(endpoint, fetchConfig);

    if (!response.ok) {
      throw new Error('Failed to get facts export download URL');
    }

    const data: DownloadUrlResponse = await response.json();

    const link = document.createElement('a');
    link.href = data.downloadUrl;
    link.download = format ? `${title}.${format}` : `${title} with source documents.zip`;
    link.click();
  };

  return {
    downloadMutation: useMutation(downloadFacts, {
      onError: (error: Error) => {
        toast.error(error.message || 'Failed to download facts. Please try again.');
      },
    }),
    downloadExport,
  };
};

export default useDownloadFacts;
